// ============================================================================
// FSA ADJUSTMENT JOURNAL MODAL  (FSA-2.2 Adjustments)
// ============================================================================
// Create / Edit / View modal for a single adjustment journal — header fields
// (number, date, narration) plus a Dr/Cr line editor with live tally indicator.
// DRAFTs may have unbalanced lines; Post requires Σ amount = 0 (paisa-accurate)
// and at least 2 lines with both Dr and Cr present.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { fsaAdjustmentJournalService } from 'api/services/fsa/fsaAdjustmentJournalService';
import { useAuditedLedgerBalances, useFsaAdjustmentJournal } from 'hooks/api/fsa/useFsaAdjustmentJournals';
import type { ID } from 'api/types';
import type {
  AdjustmentJournal,
  AdjustmentJournalLineRequest,
} from 'api/types/fsa/adjustmentJournal.types';

type Mode = 'create' | 'edit' | 'view';

interface Props {
  isOpen: boolean;
  mode: Mode;
  engagementId: ID;
  journalId?: ID | null;
  onClose: () => void;
  onSaved: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: Mode, journalId?: ID | null, instanceId?: string) => void;
  minimizeRef?: React.RefObject<(() => void) | null>;
  createInstanceId?: string;
}

interface LineRow extends AdjustmentJournalLineRequest {
  /** Local UI key. Persisted only inside React; backend re-keys on save. */
  key: string;
}

const newRow = (displayOrder: number): LineRow => ({
  key: `row-${displayOrder}-${Math.random().toString(36).slice(2, 8)}`,
  ledgerName: '',
  amount: 0,
  source: '',
  narration: '',
  displayOrder,
});

const todayISO = () => new Date().toISOString().slice(0, 10);

const FsaAdjustmentJournalModal: React.FC<Props> = ({
  isOpen,
  mode: initialMode,
  engagementId,
  journalId,
  onClose,
  onSaved,
  tabId,
  moduleName,
  onRestoreModal,
  minimizeRef,
  createInstanceId,
}) => {
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  // Live hydrate for edit/view
  const { data: hydrated, loading: hydrating, refetch: refetchHydrate } = useFsaAdjustmentJournal(
    initialMode !== 'create' ? (journalId ?? null) : null,
  );

  // Ledger pool — backend-computed list driven by audited TB. Same source the
  // TB / Note screens use, so a Dr/Cr line can reference any ledger present in
  // CONSOLIDATED or any POSTED JE.
  const { data: ledgers } = useAuditedLedgerBalances(engagementId);

  // ---- internal mode (Edit ⇄ View toggles within the modal) ----
  const [mode, setMode] = useState<Mode>(initialMode);
  useEffect(() => { setMode(initialMode); }, [initialMode, isOpen]);

  // ---- form state ----
  const [journalNumber, setJournalNumber] = useState('');
  const [journalDate, setJournalDate] = useState(todayISO());
  const [narration, setNarration] = useState('');
  const [lines, setLines] = useState<LineRow[]>([newRow(1), newRow(2)]);

  const formDataRef = useRef({ journalNumber, journalDate, narration, lines });
  formDataRef.current = { journalNumber, journalDate, narration, lines };

  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  // ---- field-level validation ----
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // ---- hydrate / reset on open ----
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    if (initialMode === 'create') {
      const init = {
        journalNumber: '',
        journalDate: todayISO(),
        narration: '',
        lines: [newRow(1), newRow(2)],
      };
      setJournalNumber(init.journalNumber);
      setJournalDate(init.journalDate);
      setNarration(init.narration);
      setLines(init.lines);
      initialSnapshotRef.current = JSON.stringify(init);
      setFieldErrors({});
      return;
    }
    // edit / view — wait for hydrate; populate on data arrival
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (initialMode === 'create') return;
    if (!hydrated) return;
    if (isRestoreRef.current) return;
    const rows: LineRow[] = (hydrated.entries ?? []).map((e, idx) => ({
      key: String(e.id),
      ledgerName: e.ledgerName,
      amount: e.amount,
      source: e.source ?? '',
      narration: e.narration ?? '',
      displayOrder: e.displayOrder ?? idx + 1,
    }));
    const init = {
      journalNumber: hydrated.journalNumber ?? '',
      journalDate: (hydrated.journalDate ?? todayISO()).slice(0, 10),
      narration: hydrated.narration ?? '',
      lines: rows.length > 0 ? rows : [newRow(1), newRow(2)],
    };
    setJournalNumber(init.journalNumber);
    setJournalDate(init.journalDate);
    setNarration(init.narration);
    setLines(init.lines);
    initialSnapshotRef.current = JSON.stringify(init);
    setFieldErrors({});
  }, [hydrated, isOpen, initialMode]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify({ journalNumber, journalDate, narration, lines }) !== initialSnapshotRef.current;
  }, [journalNumber, journalDate, narration, lines]);

  // ---- tally ----
  const tally = useMemo(() => {
    let dr = 0, cr = 0;
    for (const l of lines) {
      const amt = Number(l.amount) || 0;
      if (amt > 0) dr += amt; else if (amt < 0) cr += -amt;
    }
    const diff = Math.round((dr - cr) * 100) / 100;
    return { dr, cr, diff };
  }, [lines]);

  // FSA-2.4 — composite option label includes classification hints
  // so the auditor sees "where this ledger goes" without leaving the
  // modal. Format: `LedgerName — NoteName · ClassName (ClassCode)`.
  // Note name leads because it's the higher-context anchor an auditor
  // recognises (class codes like "N7_OTHERS" are template internals).
  // Filter / search runs against the full label, so the auditor can
  // type the note name ("Trade Payables"), class name ("Others"),
  // class code ("N7_OTHERS"), or ledger name and narrow the list.
  // For unclassified / JE-introduced ledgers the suffix flags the
  // state explicitly.
  const buildLedgerOptionLabel = useCallback((l: typeof ledgers[number]): string => {
    const parts: string[] = [l.ledgerName];
    if (l.isJeCreated && !l.scheduleClassCode && !l.scheduleCode) {
      parts.push('— JE-introduced (unclassified)');
      return parts.join(' ');
    }
    if (l.isCustomClassified) {
      parts.push(`— ${l.scheduleName ?? l.scheduleCode ?? 'Note'} · custom class`);
    } else if (l.scheduleClassName && l.scheduleName) {
      // Lead with Note name (most meaningful), then the class label,
      // then the class code in parentheses for power-users.
      parts.push(`— ${l.scheduleName} · ${l.scheduleClassName}${l.scheduleClassCode ? ` (${l.scheduleClassCode})` : ''}`);
    } else if (l.scheduleName || l.scheduleCode) {
      parts.push(`— ${l.scheduleName ?? l.scheduleCode} · pending class`);
    } else {
      parts.push('— unclassified');
    }
    return parts.join(' ');
  }, []);

  // Lookup by lowercase trimmed ledger name → audited balance row.
  // Used by the per-line balance display.
  const ledgerByName = useMemo(() => {
    const m = new Map<string, typeof ledgers[number]>();
    for (const l of ledgers) {
      m.set(l.ledgerName.trim().toLowerCase(), l);
    }
    return m;
  }, [ledgers]);
  const lookupLedger = useCallback(
    (name: string) => ledgerByName.get(name.trim().toLowerCase()),
    [ledgerByName],
  );

  // ---- ledger options (async) ----
  const ledgerOptions = useMemo(
    () => ledgers.map(l => ({
      value: l.ledgerName,
      label: buildLedgerOptionLabel(l),
    })),
    [ledgers, buildLedgerOptionLabel],
  );
  const loadLedgerOptions = useCallback((input: string) => {
    const q = input.trim().toLowerCase();
    // Filter on the full LABEL (not just value) so the auditor can
    // search by Note / Class as well as ledger name.
    const filtered = !q
      ? ledgerOptions
      : ledgerOptions.filter(o => o.label.toLowerCase().includes(q));
    // FSA-2.4 — cap raised from 200 → 1000. The previous cap silently
    // truncated dropdowns on TBs > 200 ledgers; with the backend now
    // returning alpha-ordered ledgers, the cap is purely a sanity
    // guard against pathological cases. Real-world Indian TBs sit in
    // the 100-500 range; 1000 covers any realistic engagement without
    // a perceivable dropdown lag.
    return Promise.resolve(filtered.slice(0, 1000));
  }, [ledgerOptions]);

  // ---- line ops ----
  const updateLine = useCallback((key: string, patch: Partial<LineRow>) => {
    setLines(prev => prev.map(l => (l.key === key ? { ...l, ...patch } : l)));
  }, []);
  const addLine = useCallback(() => {
    setLines(prev => [...prev, newRow(prev.length + 1)]);
  }, []);
  const removeLine = useCallback((key: string) => {
    setLines(prev => {
      if (prev.length <= 2) return prev;       // keep minimum two
      return prev.filter(l => l.key !== key).map((l, i) => ({ ...l, displayOrder: i + 1 }));
    });
  }, []);

  // ---- save / post / reverse ----
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  // FSA-2.4 Phase 2 — post/reverse confirm states retired; replaced
  // by a single delete-confirm. Reason text was specific to reversal
  // (created the reversal narration) — no longer needed.
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const validateForSave = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    if (!journalDate) errors.journalDate = 'Journal date is required.';
    // FSA-2.4 — narration is mandatory on the backend ([Required] on
    // CreateAdjustmentJournalRequest). Catch it inline so the auditor
    // sees a clear field-level message rather than a generic 400 from
    // ModelState. Trimmed-empty counts as missing.
    if (!narration || !narration.trim()) {
      errors.narration = 'Narration is required — describe the purpose of this adjustment.';
    }
    if (lines.length < 2) errors.lines = 'At least two lines are required.';
    const trimmedHasLedger = lines.filter(l => l.ledgerName && l.ledgerName.trim().length > 0);
    if (trimmedHasLedger.length < 2) errors.lines = 'At least two lines must reference a ledger.';
    if (trimmedHasLedger.some(l => !l.amount || Number(l.amount) === 0)) {
      errors.lines = 'Each populated line must have a non-zero Dr or Cr amount.';
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Scroll to the first invalid field so the auditor sees the
      // inline error immediately (matches the SampleModule blueprint).
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(`[data-field="${firstField}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input,textarea')?.focus();
        }
      });
      return false;
    }
    setFieldErrors({});
    return true;
  }, [journalDate, narration, lines]);

  const buildPayloadEntries = useCallback(
    (): AdjustmentJournalLineRequest[] =>
      lines
        .filter(l => l.ledgerName && l.ledgerName.trim().length > 0 && Number(l.amount) !== 0)
        .map((l, i) => ({
          ledgerName: l.ledgerName.trim(),
          amount: Number(l.amount),
          source: l.source?.trim() || null,
          narration: l.narration?.trim() || null,
          displayOrder: i + 1,
        })),
    [lines],
  );

  const handleSaveClick = useCallback(() => {
    if (!validateForSave()) return;
    setSaveConfirmOpen(true);
  }, [validateForSave]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const entries = buildPayloadEntries();
      if (initialMode === 'create' || (mode === 'edit' && !journalId)) {
        const r = await fsaAdjustmentJournalService.create({
          primaEngagementId: engagementId,
          journalNumber: journalNumber.trim() || null,
          journalDate,
          narration: narration.trim() || null,
          entries,
        });
        notificationService.toast.success(`Journal ${r.data?.journalNumber ?? ''} created.`);
      } else if (journalId && hydrated) {
        await fsaAdjustmentJournalService.update(journalId, {
          journalNumber: journalNumber.trim() || hydrated.journalNumber,
          journalDate,
          narration: narration.trim() || null,
          rowVersion: hydrated.rowVersion,
          entries,
        });
        notificationService.toast.success('Journal updated.');
      }
      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      notificationService.toast.error(err?.response?.data?.message ?? 'Error saving journal.');
    }
  }, [
    buildPayloadEntries, engagementId, hydrated, initialMode, journalDate, journalId,
    journalNumber, mode, narration, onClose, onSaved,
  ]);

  // FSA-2.4 Phase 2 — Post / Reverse removed from the flow. Save is
  // now strict (Dr=Cr enforced server-side) and immediately active.
  // Reversal is replaced by soft-delete.
  const handleDeleteClick = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!journalId || !hydrated) return;
    setDeleteConfirmOpen(false);
    setIsSaving(true);
    try {
      await fsaAdjustmentJournalService.remove(journalId, hydrated.rowVersion);
      notificationService.toast.success(`Journal ${hydrated.journalNumber} deleted.`);
      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      notificationService.toast.error(err?.response?.data?.message ?? 'Error deleting journal.');
    }
  }, [hydrated, journalId, onClose, onSaved]);

  const handleClose = useCallback(() => {
    if (mode !== 'view' && isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose();
  }, [isDirty, mode, onClose]);

  // ---- minimize ----
  const { minimize } = useModalMinimize();
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const journalIdRef = useRef(journalId);
  journalIdRef.current = journalId;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const currentMode = modeRef.current;
    const currentJournalId = journalIdRef.current;
    const currentInstanceId = createInstanceIdRef.current;
    const modalId = currentMode === 'create'
      ? `${tabId}-fsa-adj-journal-create-${currentInstanceId ?? '1'}`
      : `${tabId}-fsa-adj-journal-${currentMode}-${currentJournalId}`;
    const title = currentMode === 'create'
      ? `Adjustment Journal · New (${currentInstanceId ?? '1'})`
      : `Adjustment Journal · ${formDataRef.current.journalNumber || (hydrated?.journalNumber ?? '')}`;
    const savedForm = { ...formDataRef.current, lines: formDataRef.current.lines.map(l => ({ ...l })) };
    const savedSnapshot = initialSnapshotRef.current;
    const currentDirty = savedSnapshot
      ? JSON.stringify(savedForm) !== savedSnapshot
      : false;
    minimize({
      id: modalId,
      title,
      moduleName: moduleName ?? 'FSA',
      tabId,
      isDirty: currentDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setJournalNumber(savedForm.journalNumber);
        setJournalDate(savedForm.journalDate);
        setNarration(savedForm.narration);
        setLines(savedForm.lines);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreModal?.(currentMode, currentJournalId ?? null, currentInstanceId);
      },
    });
    onClose();
  }, [tabId, moduleName, hydrated, onClose, onRestoreModal, minimize]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  if (!isOpen) return null;

  // FSA-2.4 Phase 2 — strict-save workflow: no DRAFT/POSTED/REVERSED
  // distinction in the UI. A JE is either ACTIVE or DELETED. Edit /
  // delete are gated by the note-status lock (any touched Note in
  // APPROVED status → JE is read-only).
  const statusCode = hydrated?.statusCode ?? 'POSTED';
  const isDeleted = !!hydrated?.deletedAt;
  const isLockedByNote = !!hydrated?.isLockedByApprovedNote;
  // View-only triggers: view mode, deleted JE, or locked by an
  // approved note. Create mode bypasses (no hydrated state yet).
  const readOnly = mode === 'view' || isDeleted || (mode === 'edit' && isLockedByNote);
  const canEditLines = mode !== 'view' && !isDeleted && !isLockedByNote;
  // Approved notes that lock the JE — surfaces in the banner.
  const lockingNotes = (hydrated?.touchedNotes ?? []).filter(
    n => (n.noteStatusCode ?? '').toUpperCase() === 'APPROVED'
  );

  return (
    <>
      <div className="modal-backdrop" onClick={handleClose} />
      <div
        className="modal-container modal-xl"
        style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
      >
        {isSaving && (
          <div style={loadingOverlayStyle}>
            <div style={spinnerStyle} />
            <span style={spinnerLabelStyle}>Saving…</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <IconSystem.edit size={18} />
            Adjustment Journal
            {/* FSA-2.4 Phase 2 — status chip simplified to ACTIVE / DELETED.
                Legacy DRAFT / POSTED / REVERSED no longer surfaces in the UI;
                the underlying enum stays for back-compat with old rows. */}
            {hydrated && isDeleted && (
              <span style={chip('#fee2e2', '#991b1b')} title="Soft-deleted">DELETED</span>
            )}
            {hydrated && !isDeleted && (
              <span style={chip('#dcfce7', '#166534')} title="Active — counted in audited TB">ACTIVE</span>
            )}
            {hydrated && isLockedByNote && (
              <span style={chip('#fef3c7', '#92400e')} title="Locked: one or more touched Notes are APPROVED. Reopen the Note to edit.">
                🔒 Locked
              </span>
            )}
          </h2>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ overflowY: 'auto' }}>
          {hydrating && (
            <div style={{ padding: '1.5rem', color: 'var(--text-secondary)' }}>Loading journal…</div>
          )}

          {/* FSA-2.4 Phase 2 — Note-status lock banner. Shows when one or
              more touched Notes are APPROVED. Auditor's recovery path is
              to reopen the Note (existing flow on the Notes tab) and
              come back here. */}
          {isLockedByNote && lockingNotes.length > 0 && (
            <div style={lockBannerStyle} role="alert">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <IconSystem.alert size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  <div style={{ fontWeight: 700 }}>
                    Locked — touched Note{lockingNotes.length === 1 ? '' : 's'} approved
                  </div>
                  <div style={{ fontSize: '0.78rem', marginTop: 2 }}>
                    This JE affects ledgers in {lockingNotes.length === 1 ? 'this' : 'these'} approved
                    Note{lockingNotes.length === 1 ? '' : 's'}:{' '}
                    {lockingNotes.map(n => n.scheduleName).join(', ')}.
                    Reopen the Note to DRAFT (Notes tab → row action → Reopen) to enable editing or deletion here.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Deleted-state banner. Read-only context for inspecting a
              soft-deleted JE; auditor can't restore from the modal
              today (would need a backend "undelete" — out of scope). */}
          {isDeleted && (
            <div style={deletedBannerStyle} role="alert">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <IconSystem.trash size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  <div style={{ fontWeight: 700 }}>Deleted</div>
                  <div style={{ fontSize: '0.78rem', marginTop: 2 }}>
                    This JE was deleted{hydrated?.deletedAt ? ` on ${new Date(hydrated.deletedAt).toLocaleString('en-IN')}` : ''}.
                    It's excluded from the audited TB and is shown here for audit reference only.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Header fields */}
          <div className="form-grid-3">
            <div className="form-group" data-field="journalNumber">
              <label className="form-label">Journal Number</label>
              <input
                type="text"
                className="form-input"
                value={journalNumber}
                onChange={(e) => setJournalNumber(e.target.value)}
                placeholder="ADJ-001 (auto if blank)"
                disabled={readOnly}
              />
            </div>
            <div className="form-group" data-field="journalDate">
              <label className="form-label">Journal Date <span className="text-danger">*</span></label>
              <input
                type="date"
                className={`form-input${getFieldError('journalDate') ? ' form-field-error' : ''}`}
                value={journalDate}
                onChange={(e) => { clearFieldError('journalDate'); setJournalDate(e.target.value); }}
                disabled={readOnly}
              />
              {getFieldError('journalDate') && <div className="form-error-message">{getFieldError('journalDate')}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Posted by / on</label>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                {hydrated?.postedAt
                  ? `${hydrated.postedByName ?? '—'} · ${formatDate(hydrated.postedAt)}`
                  : '—'}
              </div>
            </div>
          </div>

          <div className="form-group" data-field="narration">
            <label className="form-label">
              Narration <span className="text-danger">*</span>
            </label>
            <textarea
              className={`form-textarea${getFieldError('narration') ? ' form-field-error' : ''}`}
              value={narration}
              onChange={(e) => {
                clearFieldError('narration');
                setNarration(e.target.value);
              }}
              rows={2}
              placeholder="e.g. Provision for audit fees, Reclass of suspense balance"
              disabled={readOnly}
            />
            {getFieldError('narration') && (
              <div className="form-error-message">{getFieldError('narration')}</div>
            )}
          </div>

          {/* FSA-2.4 Phase 2 — Reversal-reason textarea removed.
              Reversal is replaced by soft-delete + the existing
              narration field above carries the audit context. */}

          {/* Lines */}
          <div style={{ marginTop: '0.75rem' }}>
            <div style={linesHeaderRowStyle}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>Lines</div>
              <div style={{ flex: 1 }} />
              {canEditLines && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={addLine}>
                  <IconSystem.add size={14} /> Add Line
                </button>
              )}
            </div>

            {fieldErrors.lines && (
              <div className="form-error-message" style={{ marginBottom: 6 }}>{fieldErrors.lines}</div>
            )}

            <div style={lineGridContainer}>
              <div style={lineGridHeaderStyle}>
                <div>#</div>
                <div>Ledger</div>
                <div style={{ textAlign: 'right' }}>Debit</div>
                <div style={{ textAlign: 'right' }}>Credit</div>
                <div>Source</div>
                <div>Narration</div>
                <div></div>
              </div>
              {lines.map((line, idx) => {
                const amt = Number(line.amount) || 0;
                const dr = amt > 0 ? amt : 0;
                const cr = amt < 0 ? -amt : 0;
                return (
                  <div key={line.key} style={lineGridRowStyle}>
                    <div style={lineGridIdxStyle}>{idx + 1}</div>
                    <div>
                      <SyutSelect
                        isAsync
                        loadOptions={loadLedgerOptions}
                        defaultOptions={true}
                        cacheOptions={false}
                        // Seed with the classification-enriched label
                        // when available, so edit-mode rows show the
                        // same Note/Class context as fresh picks.
                        seedOptions={(() => {
                          if (!line.ledgerName) return [];
                          const known = lookupLedger(line.ledgerName);
                          return [{
                            value: line.ledgerName,
                            label: known ? buildLedgerOptionLabel(known) : line.ledgerName,
                          }];
                        })()}
                        value={line.ledgerName || undefined}
                        onChange={(v) => updateLine(line.key, { ledgerName: typeof v === 'string' ? v : '' })}
                        // FSA-2.2 — let the auditor type a brand-new ledger
                        // name on a JE line. When the JE is posted, the
                        // backend's EnsureJeIntroducedLedgersAsync inserts
                        // a zero-balance `is_je_created` row into the
                        // consolidated TB so the new ledger can be
                        // classified via the existing flow. No separate
                        // "create ledger" UI needed — typing IS the
                        // creation.
                        isCreatable
                        onCreateOption={(inputValue) => {
                          const trimmed = inputValue.trim();
                          if (trimmed.length > 0) {
                            updateLine(line.key, { ledgerName: trimmed });
                          }
                        }}
                        formatCreateLabel={(inputValue) => (
                          <span>
                            <span style={{ fontWeight: 600 }}>+ Add new ledger:</span>{' '}
                            <span>"{inputValue}"</span>
                          </span>
                        )}
                        isClearable
                        size="sm"
                        placeholder="Pick or type new ledger…"
                        isDisabled={!canEditLines}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPlacement="auto"
                      />
                      {/* FSA-2.4 — current + revised balance below the
                          picker. Surfaces the impact of the entry
                          immediately so the auditor catches direction
                          errors before save. Shows only when a known
                          ledger is selected; JE-introduced new ledgers
                          start at 0. */}
                      {(() => {
                        const known = line.ledgerName ? lookupLedger(line.ledgerName) : null;
                        if (!line.ledgerName) return null;
                        const current = known?.audited ?? 0;
                        const delta = Number(line.amount) || 0;
                        const revised = current + delta;
                        const fmt = (n: number) => {
                          const abs = Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                          return n < -0.005 ? `${abs} Cr` : n > 0.005 ? `${abs} Dr` : '0.00';
                        };
                        return (
                          <div style={balanceHintStyle}>
                            <span title="Current audited balance (consolidated + posted JEs)">
                              <span style={balanceHintLabelStyle}>Current</span>{' '}
                              <span style={balanceHintValueStyle}>{fmt(current)}</span>
                            </span>
                            {delta !== 0 && (
                              <>
                                <span style={balanceHintArrowStyle}>→</span>
                                <span title="Revised balance after applying this entry">
                                  <span style={balanceHintLabelStyle}>Revised</span>{' '}
                                  <span style={{
                                    ...balanceHintValueStyle,
                                    color: revised === 0
                                      ? 'var(--text-secondary)'
                                      : revised > 0 ? '#1e40af' : '#991b1b',
                                  }}>{fmt(revised)}</span>
                                </span>
                              </>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div>
                      <input
                        type="number"
                        className="form-input"
                        style={amountInputStyle}
                        value={dr || ''}
                        onChange={(e) => {
                          const v = Number(e.target.value) || 0;
                          updateLine(line.key, { amount: v });
                        }}
                        placeholder="0.00"
                        step="0.01"
                        disabled={!canEditLines}
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        className="form-input"
                        style={amountInputStyle}
                        value={cr || ''}
                        onChange={(e) => {
                          const v = Number(e.target.value) || 0;
                          updateLine(line.key, { amount: -v });
                        }}
                        placeholder="0.00"
                        step="0.01"
                        disabled={!canEditLines}
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        className="form-input"
                        value={line.source ?? ''}
                        onChange={(e) => updateLine(line.key, { source: e.target.value })}
                        placeholder="Branch / Div"
                        disabled={!canEditLines}
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        className="form-input"
                        value={line.narration ?? ''}
                        onChange={(e) => updateLine(line.key, { narration: e.target.value })}
                        placeholder="Line narration"
                        disabled={!canEditLines}
                      />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      {canEditLines && lines.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeLine(line.key)}
                          style={removeLineBtnStyle}
                          title="Remove line"
                        >
                          <IconSystem.trash size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Totals row */}
              <div style={lineGridTotalsRowStyle}>
                <div></div>
                <div style={{ textAlign: 'right', fontWeight: 600 }}>Totals</div>
                <div style={amountTotalStyle}>{formatAmount(tally.dr)}</div>
                <div style={amountTotalStyle}>{formatAmount(tally.cr)}</div>
                <div></div>
                <div style={{ textAlign: 'right', fontSize: '0.8125rem' }}>
                  Difference
                </div>
                <div></div>
              </div>
              <div style={lineGridTotalsRowStyle}>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div style={{ textAlign: 'right' }}>
                  <span style={Math.abs(tally.diff) > 0.005
                    ? chip('#fef2f2', '#dc2626')
                    : chip('#dcfce7', '#166534')
                  }>
                    {Math.abs(tally.diff) > 0.005 ? '⚠ ' : '✓ '}
                    {formatAmount(tally.diff)}
                  </span>
                </div>
                <div></div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer — FSA-2.4 Phase 2: strict-save flow. Single [Save]
            button (was Save Draft + Post). [Delete] surfaces in edit
            mode for non-deleted, non-locked JEs. View mode + deleted
            state both render a single [Close] action. */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          {mode === 'view' || isDeleted ? (
            <button className="btn btn-primary" onClick={onClose} disabled={isSaving}>
              Close
            </button>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>
                Cancel
              </button>
              {mode === 'edit' && hydrated && !isLockedByNote && (
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteClick}
                  disabled={isSaving}
                  title="Delete this JE (soft-delete; recoverable from the deleted-JE list)"
                >
                  Delete
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={handleSaveClick}
                disabled={isSaving || isLockedByNote}
                title={isLockedByNote
                  ? 'Locked — reopen the approved Note to edit'
                  : 'Save the journal (immediately affects audited TB)'}
              >
                Save
              </button>
            </>
          )}
        </div>
      </div>

      {/* FSA-2.4 Phase 2 — Save confirm now warns explicitly that the
          JE goes live immediately (no draft holding pen). Post / Reverse
          confirm dialogs retired with the workflow simplification. */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title="Save Adjustment Journal"
        message={initialMode === 'create'
          ? 'Save this journal? It will be active immediately and reflected in the audited Trial Balance, BS / P&L / CF and Notes.'
          : 'Save changes to this journal? Updates take effect immediately on the audited Trial Balance and related Notes.'}
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Adjustment Journal"
        message={`Delete journal ${hydrated?.journalNumber ?? ''}? The JE will be soft-deleted (recoverable from the deleted-JE list). It will no longer affect the audited Trial Balance.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// helpers + styles
// ---------------------------------------------------------------------------

const formatDate = (iso: string): string => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};
const formatAmount = (n: number): string =>
  Math.abs(n) < 0.005
    ? '0.00'
    : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const statusBg = (s: string): string =>
  s === 'POSTED' ? '#dcfce7' : s === 'REVERSED' ? '#fee2e2' : '#dbeafe';
const statusFg = (s: string): string =>
  s === 'POSTED' ? '#166534' : s === 'REVERSED' ? '#991b1b' : '#1e40af';

const chip = (bg: string, color: string): React.CSSProperties => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: 999,
  background: bg, color, fontSize: '0.7rem', fontWeight: 600,
});

// FSA-2.4 Phase 2 — banners for the JE-level lock + deleted state.
// Block layout sits directly under the modal header, full-width.
const lockBannerStyle: React.CSSProperties = {
  padding: '10px 14px',
  marginBottom: 12,
  background: '#fef3c7',
  border: '1px solid #fcd34d',
  borderLeft: '4px solid #d97706',
  borderRadius: 4,
  color: '#78350f',
  fontSize: '0.82rem',
  lineHeight: 1.4,
};
const deletedBannerStyle: React.CSSProperties = {
  padding: '10px 14px',
  marginBottom: 12,
  background: '#fee2e2',
  border: '1px solid #fca5a5',
  borderLeft: '4px solid #b91c1c',
  borderRadius: 4,
  color: '#7f1d1d',
  fontSize: '0.82rem',
  lineHeight: 1.4,
};

const loadingOverlayStyle: React.CSSProperties = {
  position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
};
const spinnerStyle: React.CSSProperties = {
  width: '2rem', height: '2rem',
  border: '3px solid var(--border-color)',
  borderTopColor: '#2563eb', borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
};
const spinnerLabelStyle: React.CSSProperties = {
  fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500,
};

const linesHeaderRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', marginBottom: 6,
};
const lineGridContainer: React.CSSProperties = {
  border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden',
};
const lineGridColumns = '40px minmax(180px, 1.4fr) 130px 130px 120px minmax(180px, 1.4fr) 40px';
const lineGridHeaderStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: lineGridColumns, gap: 8,
  padding: '6px 8px', background: 'var(--bg-secondary)',
  fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--border-color)',
};
const lineGridRowStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: lineGridColumns, gap: 8,
  padding: '6px 8px', borderBottom: '1px solid var(--border-color)',
  alignItems: 'center',
};
const lineGridTotalsRowStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: lineGridColumns, gap: 8,
  padding: '6px 8px', background: 'var(--bg-secondary)',
  fontSize: '0.8125rem',
};
const lineGridIdxStyle: React.CSSProperties = {
  textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)',
};
const amountInputStyle: React.CSSProperties = {
  textAlign: 'right', fontVariantNumeric: 'tabular-nums',
};
const amountTotalStyle: React.CSSProperties = {
  textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums',
};
const removeLineBtnStyle: React.CSSProperties = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: 'var(--text-secondary)', padding: 4,
};

// FSA-2.4 — Current → Revised balance hint below the ledger picker.
// Inline single-line strip; values right-aligned with monospaced
// tabular figures so the auditor can compare across rows visually.
const balanceHintStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginTop: 3,
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
  flexWrap: 'wrap',
};
const balanceHintLabelStyle: React.CSSProperties = {
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  fontWeight: 700,
  fontSize: '0.62rem',
};
const balanceHintValueStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 600,
  color: 'var(--text-primary)',
};
const balanceHintArrowStyle: React.CSSProperties = {
  color: 'var(--text-secondary)',
  fontWeight: 400,
};

export default FsaAdjustmentJournalModal;
